import { useLazyQuery, useQuery } from '@apollo/client';
import { EmptyTablePlaceholder } from '@openx/components-core/lib/TablePlaceholder/EmptyTablePlaceholder';
import {
  type ByValueFilter,
  serverSideCriteriaProcessing,
  sortStage,
} from '@openx/components-table/serverSideCriteriaProcessing';
import { Table, TableName } from '@openx/components-table/Table';
import type { QueryVar } from '@openx/types/serverSideCriteria';
import { CriteriaDimension, SortDirection } from '@openx/types/tableCriteria';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { type FC, type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { TABLE_PAGE_SIZE } from '../../../config';
import {
  GetTaxonomyDocument,
  type GetTaxonomyQueryVariables,
} from '../../../graphql/query/taxonomy/GetTaxonomy.generated';
import { GetTaxonomyMetadataDocument } from '../../../graphql/query/taxonomy/GetTaxonomyMetadata.generated';
import { GetTaxonomySearchTotalDocument } from '../../../graphql/query/taxonomy/GetTaxonomySearchTotal.generated';
import { type DefaultTableCriteria, useListCriteria } from '../../../utils/useListCriteria';
import { VALID_TAXONOMY_CATEGORIES } from '../constants';
import type { TaxonomyRow } from '../types';
import { mapTaxonomyMetadataToTree, recursiveTreeSort } from '../utils/mapSegmentsToTree';
import { useFiltersConfig } from '../utils/useFiltersConfig';
import { TaxonomyTopBar } from './TaxonomyTopBar';
import { tableColumns } from './tableColumns';

const defaultCriteria: DefaultTableCriteria<'taxonomy'> = {
  [CriteriaDimension.FILTERS]: {
    phrase: '',
    segment_category: '',
    segment_source: '',
    segment_sub_category: '',
    segment_type: '',
  },
  [CriteriaDimension.SORT]: {
    column: 'name',
    direction: SortDirection.ASC,
    sortableColumns: ['name', 'segment_sub_category', 'segment_category', 'segment_source'],
  },
  [CriteriaDimension.PAGINATION]: {
    pageNumber: 1,
    pageSize: TABLE_PAGE_SIZE,
    totalCount: 100,
  },
};

type QueryParams = GetTaxonomyQueryVariables & QueryVar;

const defaultWhereParams: QueryParams['where'] = {
  category: { _in: [...VALID_TAXONOMY_CATEGORIES] },
  is_archived: { _eq: false },
  is_leaf: { _eq: true },
  name: { _neq: 'All' },
};

export const TaxonomyList: FC = (): JSX.Element => {
  const { criteria, setCriteria } = useListCriteria('taxonomy', defaultCriteria);
  const [tableData, setTableData] = useState<TaxonomyRow[]>([]);

  const filters = useMemo(() => {
    const byValue = Object.entries(omit(criteria.filters, 'phrase')).reduce((acc, [field, values]) => {
      if (values) {
        acc.push({
          field,
          values: values?.split(','),
        });
      }

      return acc;
    }, [] as ByValueFilter);

    const byPhrase = {
      fields: ['name', 'description'],
      phrase: criteria.filters?.phrase ?? '',
    };

    return { byPhrase, byValue };
  }, [criteria]);

  const searchApplied = useMemo(
    () => !!filters.byPhrase?.phrase || filters.byValue?.some(({ values }) => !isEmpty(values)),
    [filters]
  );

  const handleCriteriaChange = useCallback(
    newCriteria => {
      if (
        newCriteria?.dimension === 'sort' &&
        newCriteria.value?.column === criteria.sort?.column &&
        newCriteria.value?.direction === 'asc' &&
        newCriteria.value?.column !== defaultCriteria.sort?.column
      ) {
        newCriteria.value.column = defaultCriteria.sort?.column;
        newCriteria.value.direction = defaultCriteria.sort?.direction;
      }

      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);
      if (!isEqual(newCriteriaProcessed, criteria)) {
        setCriteria(newCriteriaProcessed);
      }
    },
    [setCriteria, criteria]
  );

  const {
    loading: metadataLoading,
    error: metadataError,
    data: { segment_aggregate: { nodes: metadata = [] } = {} } = {},
    refetch: metadataRefetch,
  } = useQuery(GetTaxonomyMetadataDocument);

  const [getSearch, { error: searchError, loading: searchLoading, data: { segment: segmentSearch = [] } = {} }] =
    useLazyQuery(GetTaxonomyDocument);

  const [getSegmentsSearchTotal, { loading: searchTotalLoading, error: searchTotalError }] = useLazyQuery(
    GetTaxonomySearchTotalDocument,
    {
      onCompleted({ segment_aggregate }) {
        if (
          typeof criteria.pagination?.totalCount !== 'undefined' &&
          typeof segment_aggregate?.aggregate?.count !== 'undefined' &&
          criteria.pagination?.totalCount !== segment_aggregate.aggregate.count
        ) {
          criteria.pagination.totalCount = segment_aggregate.aggregate.count;
          setCriteria({ ...criteria });
        }
      },
    }
  );

  const [getSubOptions] = useLazyQuery(GetTaxonomyDocument);

  const taxonomyMetadataTree = useMemo(() => {
    if (searchApplied) return [];
    if (isEqual(criteria.sort, defaultCriteria.sort)) {
      return mapTaxonomyMetadataToTree(metadata);
    } else {
      return recursiveTreeSort({
        criteria,
        setCriteria,
        sotringMethod: clientSideCriteriaProcessing,
        tree: mapTaxonomyMetadataToTree(metadata),
      });
    }
  }, [setCriteria, metadata, criteria, searchApplied]);
  const filtersConfig = useFiltersConfig(metadata);

  const fetchSubOptions = useCallback(
    async data => {
      const fetchedChildren = await getSubOptions({
        variables: {
          order_by: sortStage(criteria.sort),
          where: {
            ...defaultWhereParams,
            segment_category: { _eq: data.segment_category },
            segment_sub_category: data.segment_sub_category ? { _eq: data.segment_sub_category } : { _is_null: true },
            segment_type: { _eq: data.segment_type },
          },
        },
      });

      const sortedChildSegments: TaxonomyRow[] = clientSideCriteriaProcessing(
        fetchedChildren?.data?.segment ?? [],
        criteria,
        {
          onCriteriaAdjust: setCriteria,
        }
      );

      return sortedChildSegments;
    },
    [getSubOptions, setCriteria, criteria]
  );

  useEffect(() => {
    if (!metadataLoading && !searchApplied) {
      setTableData(taxonomyMetadataTree);
    }
  }, [metadataLoading, taxonomyMetadataTree, searchApplied]);

  useEffect(() => {
    if (!searchLoading && searchApplied) {
      setTableData(segmentSearch);
    }
  }, [searchLoading, segmentSearch, searchApplied]);

  useEffect(() => {
    if (searchApplied) {
      setCriteria(prevCriteria => ({
        ...prevCriteria,
        pagination: {
          pageNumber: prevCriteria?.pagination?.pageNumber ?? 1,
          pageSize: TABLE_PAGE_SIZE,
          totalCount: 50,
        },
      }));
    } else if (!searchApplied) {
      setCriteria(prevCriteria => ({ ...prevCriteria, pagination: undefined }));
    }
  }, [setCriteria, searchApplied]);

  useEffect(() => {
    if (searchApplied) {
      const queryCrit = serverSideCriteriaProcessing<QueryParams>({ ...criteria, filters }, [defaultCriteria.sort]);

      const where = { ...defaultWhereParams, ...(queryCrit.where ?? {}) };
      getSegmentsSearchTotal({ variables: { where } });

      getSearch({ variables: { ...queryCrit, where } });
    } else {
      metadataRefetch();
    }
  }, [getSearch, getSegmentsSearchTotal, metadataRefetch, criteria, filters, searchApplied]);

  const isTableEmpty = metadata.length === 0 && segmentSearch.length === 0;

  const isTableLoading = metadataLoading || searchLoading || searchTotalLoading;

  if (metadataError || searchError || searchTotalError) throw new Error('Taxonomy error');

  if (isTableLoading) {
    return (
      <>
        <TaxonomyTopBar criteria={criteria} filtersConfig={filtersConfig} onChange={handleCriteriaChange} />
        <Table
          name={TableName.TAXONOMY_LISTS}
          data={[]}
          columns={tableColumns}
          loading={true}
          isTree={!searchApplied}
          criteria={criteria}
          highlightRules={filters.byPhrase}
          onCriteriaChange={handleCriteriaChange}
          fetchTreeChildren={fetchSubOptions}
          noItemsLabel={() => 'No segments found'}
        />
      </>
    );
  }

  if (isTableEmpty) {
    return <EmptyTablePlaceholder />;
  }

  return (
    <>
      <TaxonomyTopBar criteria={criteria} filtersConfig={filtersConfig} onChange={handleCriteriaChange} />
      <Table
        name={TableName.TAXONOMY_LISTS}
        data={tableData}
        columns={tableColumns}
        loading={isTableLoading}
        isTree={!searchApplied}
        criteria={criteria}
        highlightRules={filters.byPhrase}
        onCriteriaChange={handleCriteriaChange}
        fetchTreeChildren={fetchSubOptions}
        noItemsLabel={() => 'No segments found'}
      />
    </>
  );
};
